import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Tooltip,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Skeleton,
  Tabs,
  Tab,
  Button,
  CircularProgress,
  Collapse,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
} from 'recharts';
import {
  getStreamMap,
  getStreamIterationMetrics,
  getTeamIterationKpis,
  getAggregatedIterationKpis,
  getLastRefreshedAt,
  getWorkItemBreakdown,
  refreshIterationKpis,
  flattenTeams,
} from '../../services/sprintMetricsService';
import SprintReportDashboard from '../dashboard/SprintReportDashboard';
import SprintReviewTab from './SprintReviewTab';
import SprintHealthTab from './sprintHealth/SprintHealthTab';
import QuarterlyCommitmentTab from './sprintHealth/QuarterlyCommitmentTab';
import { getSprintHealthConfig } from '../../services/sprintHealthService';
import GovernanceTrackingTab from './GovernanceTrackingTab'; // Named "Governance" in the component

const ITERATION_COUNTS = [6, 12, 18, 24];

const CHART_COLORS = {
  planned:   '#1976d2',
  completed: '#2e7d32',
  missed:    '#d32f2f',
  creep:     '#f57c00',
};

const fmt = (n) => (n == null ? '—' : Math.round(Number(n)).toString());

const completionTextColor = (rate) => {
  if (rate >= 85) return '#2e7d32';
  if (rate >= 60) return '#f57c00';
  return '#d32f2f';
};

const getPlanningState = (planned, completed) => {
  if (!planned || planned === 0) return null;
  const ratio = (completed / planned) * 100;
  if (ratio >= 90 && ratio <= 110) return 'good';
  if (ratio > 110) return 'undercommitted';
  return 'overcommitted';
};

const PLANNING_CONFIG = {
  good:           { label: 'Good',           color: '#2e7d32', bg: 'rgba(46,125,50,0.08)'  },
  undercommitted: { label: 'Undercommitted', color: '#f57c00', bg: 'rgba(245,124,0,0.08)'  },
  overcommitted:  { label: 'Overcommitted',  color: '#d32f2f', bg: 'rgba(211,47,47,0.08)'  },
};

const PlanningChip = ({ planned, completed }) => {
  const state = getPlanningState(planned, completed);
  if (!state) return <Typography variant="caption" color="text.disabled">—</Typography>;
  const { label, color, bg } = PLANNING_CONFIG[state];
  return (
    <Typography
      variant="caption"
      sx={{ fontWeight: 600, color, bgcolor: bg, px: 1, py: 0.25, borderRadius: 1, whiteSpace: 'nowrap' }}
    >
      {label}
    </Typography>
  );
};

const SummaryCard = ({ label, value, color, subtitle, pct, valueSize = '2rem' }) => (
  <Paper variant="outlined" sx={{ p: 2, height: 90, width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.7rem', lineHeight: 1 }}>{label}</Typography>
      {subtitle && <Typography variant="caption" color="text.disabled" sx={{ display: 'block', fontSize: '0.6rem', lineHeight: 1.2 }}>{subtitle}</Typography>}
    </Box>
    {pct != null ? (
      <Box display="flex" alignItems="flex-end" justifyContent="space-between">
        <Typography sx={{ fontWeight: 700, color, lineHeight: 1, fontSize: valueSize }}>{value}</Typography>
        <Typography sx={{ fontWeight: 700, color, lineHeight: 1, fontSize: valueSize, opacity: 0.85 }}>{pct}%</Typography>
      </Box>
    ) : (
      <Typography sx={{ fontWeight: 700, color, lineHeight: 1, fontSize: valueSize }}>{value}</Typography>
    )}
  </Paper>
);

// ── Iteration Metrics tab ─────────────────────────────────────────────────────
const IterationMetricsTab = () => {
  const [team, setTeam]           = useState('');
  const [stream, setStream]       = useState('');
  const [count, setCount]         = useState(12);
  const [rows, setRows]           = useState([]);
  const [loading, setLoading]     = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError]         = useState(null);
  const [refreshError, setRefreshError] = useState(null);
  const [streamMap, setStreamMap] = useState({});
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);
  const [tableOpen, setTableOpen]                   = useState(false);
  const [breakdownTableOpen, setBreakdownTableOpen] = useState(false);
  const [breakdown, setBreakdown]                   = useState([]);
  const [breakdownMode, setBreakdownMode]           = useState('numbers');

  useEffect(() => {
    getStreamMap().then(setStreamMap).catch(() => {});
    getLastRefreshedAt().then(setLastRefreshedAt).catch(() => {});
  }, []);

  const streamNames = Object.keys(streamMap).sort();
  const allTeams = useMemo(() => flattenTeams(streamMap), [streamMap]);

  const handleStreamChange = (value) => { setStream(value); setTeam(''); };
  const handleTeamChange   = (value) => { setTeam(value);   setStream(''); };

  // ── Determine which team IDs are "active" for the current filter ────────────
  const activeTeamIds = stream && streamMap[stream]
    ? streamMap[stream].map(t => t.id)
    : team
      ? [team]
      : allTeams;

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let data;
      if (activeTeamIds.length === 1) {
        data = await getTeamIterationKpis(activeTeamIds[0], count);
      } else {
        data = await getAggregatedIterationKpis(activeTeamIds, count);
      }
      setRows(data);
    } catch (err) {
      // If iteration-kpis collection is empty, surface a helpful message
      setError(err.message.includes('Failed') ? 'No data yet — click "Refresh from ADO" to load KPIs.' : err.message);
    } finally {
      setLoading(false);
    }
  }, [team, stream, count, streamMap]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchBreakdown = useCallback(async () => {
    try {
      const data = await getWorkItemBreakdown(activeTeamIds, count);
      setBreakdown(data);
    } catch (e) {
      console.warn('Work item breakdown unavailable:', e.message);
    }
  }, [team, stream, count, streamMap]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchData(); fetchBreakdown(); }, [fetchData, fetchBreakdown]);

  // ── Refresh All from ADO ─────────────────────────────────────────────────────
  const handleRefreshAll = async () => {
    setRefreshing(true);
    setRefreshError(null);
    try {
      const result = await refreshIterationKpis(null, count);
      const errs = result.errors || [];
      if (errs.length > 0) setRefreshError(`Completed with ${errs.length} error(s): ${errs.slice(0, 3).join('; ')}`);
      const ts = await getLastRefreshedAt();
      setLastRefreshedAt(ts);
      await fetchData();
    } catch (err) {
      setRefreshError(err.message);
    } finally {
      setRefreshing(false);
    }
  };

  // Table shows newest sprint first; chart keeps oldest→newest (left to right)
  const tableRows = [...rows].reverse();

  // ── Aggregates ───────────────────────────────────────────────────────────────
  const totals = rows.reduce(
    (acc, r) => ({
      planned:   acc.planned   + (r.plannedPoints    || 0),
      completed: acc.completed + (r.completedPoints  || 0),
      missed:    acc.missed    + (r.missedMovedPoints || 0),
      creep:     acc.creep     + (r.scopeCreepPoints  || 0),
    }),
    { planned: 0, completed: 0, missed: 0, creep: 0 }
  );

  const plannedRows = rows.filter(r => (r.plannedPoints || 0) > 0);
  const avgSayDo = plannedRows.length > 0
    ? Math.round(plannedRows.reduce((s, r) => s + (r.completionRate || 0), 0) / plannedRows.length * 10) / 10
    : 0;

  const chartData = rows.map(r => ({
    name:      r.sprintName.replace(/^.*?_S(\d+)_.*/i, 'S$1').slice(0, 14),
    fullName:  r.sprintName,
    Planned:   +(r.plannedPoints    || 0).toFixed(1),
    Completed: +(r.completedPoints  || 0).toFixed(1),
    Missed:    +(r.missedMovedPoints || 0).toFixed(1),
    Creep:     +(r.scopeCreepPoints  || 0).toFixed(1),
  }));

  // Align breakdown data to the same sprint labels as chartData for consistent x-axis
  const breakdownChartData = breakdown.map(r => {
    const totalSP  = (r.pbiSP || 0) + (r.productionBugSP || 0) + (r.internalBugSP || 0);
    const totalCnt = (r.pbiCount || 0) + (r.productionBugCount || 0) + (r.internalBugCount || 0);
    const pct = (v, tot) => tot > 0 ? +((v / tot) * 100).toFixed(1) : 0;
    return {
      name:     r.sprintName.replace(/^(\d{2})(\d{2})_S(\d+)_.*/i, "'$2 S$3"),
      fullName: r.sprintName,
      // numbers
      'PBI SP':              +(r.pbiSP || 0).toFixed(1),
      'Production Bug SP':   +(r.productionBugSP || 0).toFixed(1),
      'Internal Bug SP':     +(r.internalBugSP || 0).toFixed(1),
      'PBI Count':           r.pbiCount || 0,
      'Production Bug Count':r.productionBugCount || 0,
      'Internal Bug Count':  r.internalBugCount || 0,
      // percentages
      'PBI SP %':              pct(r.pbiSP || 0, totalSP),
      'Production Bug SP %':   pct(r.productionBugSP || 0, totalSP),
      'Internal Bug SP %':     pct(r.internalBugSP || 0, totalSP),
      'PBI Count %':           pct(r.pbiCount || 0, totalCnt),
      'Production Bug Count %':pct(r.productionBugCount || 0, totalCnt),
      'Internal Bug Count %':  pct(r.internalBugCount || 0, totalCnt),
    };
  });

  const isPct = breakdownMode === 'percentages';

  const trendData = rows.map(r => ({
    name:     r.sprintName.replace(/^\d{2}(\d{2})_S(\d+)_.*/i, "'$1 S$2"),
    fullName: r.sprintName,
    'Say-Do %':       +(r.completionRate || 0).toFixed(1),
    'Scope Creep SP': +(r.scopeCreepPoints  || 0).toFixed(1),
    'Missed SP':      +(r.missedMovedPoints || 0).toFixed(1),
  }));

  const subtitle = stream
    ? `Aggregated across all teams in stream: ${stream}`
    : team ? `Team: ${team}` : `Aggregated across all teams`;

  return (
    <Box sx={{ p: 3 }}>

      {/* Controls row */}
      <Box display="flex" gap={2} mb={3} flexWrap="wrap" alignItems="center">

        {/* Stream */}
        {streamNames.length > 0 && (
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Stream</InputLabel>
            <Select value={stream} label="Stream" onChange={e => handleStreamChange(e.target.value)}>
              <MenuItem value=""><em>— Individual team —</em></MenuItem>
              {streamNames.map(s => (
                <MenuItem key={s} value={s}>
                  {s}
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    ({(streamMap[s] || []).length} teams)
                  </Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Team */}
        <FormControl size="small" sx={{ minWidth: 220 }} disabled={!!stream}>
          <InputLabel>Team</InputLabel>
          <Select value={team} label="Team" onChange={e => handleTeamChange(e.target.value)}>
            <MenuItem value=""><em>All teams</em></MenuItem>
            {allTeams.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </Select>
        </FormControl>

        {/* Iterations */}
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Iterations</InputLabel>
          <Select value={count} label="Iterations" onChange={e => setCount(e.target.value)}>
            {ITERATION_COUNTS.map(n => <MenuItem key={n} value={n}>Last {n} sprints</MenuItem>)}
          </Select>
        </FormControl>

        {/* Active stream chip */}
        {stream && streamMap[stream] && (
          <Chip
            label={`${stream} · ${streamMap[stream].length} teams`}
            size="small" color="primary" variant="outlined"
            onDelete={() => handleStreamChange('')}
          />
        )}

        {/* Spacer */}
        <Box flex={1} />

        {/* Last refreshed indicator */}
        {lastRefreshedAt && (
          <Typography variant="caption" color="text.secondary" alignSelf="center">
            Last refreshed: {new Date(lastRefreshedAt).toLocaleString()}
          </Typography>
        )}

        {/* Refresh All button */}
        <Button
          variant="contained"
          size="small"
          startIcon={refreshing ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon />}
          onClick={handleRefreshAll}
          disabled={refreshing}
        >
          {refreshing ? 'Refreshing…' : 'Refresh from ADO'}
        </Button>
      </Box>

      {/* Subtitle */}
      <Typography variant="body2" color="text.secondary" mb={2}>{subtitle}</Typography>

      {error        && <Alert severity="info"  sx={{ mb: 2 }}>{error}</Alert>}
      {refreshError && <Alert severity="warning" sx={{ mb: 2 }}>Refresh completed with issues: {refreshError}</Alert>}

      {/* Summary cards */}
      {loading ? (
        <Grid container spacing={2} mb={3}>
          {[0,1,2,3,4,5].map(i => <Grid item xs={6} sm={4} md={2} key={i}><Skeleton variant="rectangular" height={90} /></Grid>)}
        </Grid>
      ) : rows.length > 0 && (() => {
        const planningState = getPlanningState(totals.planned, totals.completed);
        const planningCfg   = planningState ? PLANNING_CONFIG[planningState] : null;
        return (
          <Grid container spacing={2} mb={3} alignItems="stretch">
            <Grid item xs={6} sm={4} md={2} sx={{ display: 'flex' }}>
              <SummaryCard label="Total Planned SP" value={fmt(totals.planned)} color={CHART_COLORS.planned} />
            </Grid>
            <Grid item xs={6} sm={4} md={2} sx={{ display: 'flex' }}>
              <SummaryCard
                label="Total Completed SP"
                value={fmt(totals.completed)}
                color={CHART_COLORS.completed}
                pct={totals.planned > 0 ? Math.round(totals.completed / totals.planned * 100) : null}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2} sx={{ display: 'flex' }}>
              <SummaryCard
                label="Total Missed / Moved"
                value={fmt(totals.missed)}
                color={CHART_COLORS.missed}
                pct={totals.planned > 0 ? Math.round(totals.missed / totals.planned * 100) : null}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2} sx={{ display: 'flex' }}>
              <SummaryCard
                label="Total Scope Creep SP"
                value={fmt(totals.creep)}
                color={CHART_COLORS.creep}
                pct={totals.planned > 0 ? Math.round(totals.creep / totals.planned * 100) : null}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2} sx={{ display: 'flex' }}>
              <SummaryCard
                label="Avg Say-Do Ratio"
                value={`${avgSayDo}%`}
                color={completionTextColor(avgSayDo)}
                subtitle={`${plannedRows.length} of ${rows.length} sprints with planned work`}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2} sx={{ display: 'flex' }}>
              <Paper variant="outlined" sx={{ p: 2, height: 90, width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', bgcolor: planningCfg ? planningCfg.bg : undefined }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.7rem', lineHeight: 1 }}>Planning</Typography>
                {planningCfg ? (
                  <Typography sx={{ fontWeight: 700, color: planningCfg.color, lineHeight: 1, fontSize: '1.25rem' }}>
                    {planningCfg.label}
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.disabled">No data</Typography>
                )}
              </Paper>
            </Grid>
          </Grid>
        );
      })()}

      {/* Bar chart */}
      {!loading && rows.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>Story Points per Sprint</Typography>
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={chartData} margin={{ top: 4, right: 16, bottom: 40, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" angle={-45} textAnchor="end" interval={0} tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <RechartsTooltip
                formatter={(val, key) => [`${val} SP`, key]}
                labelFormatter={(_, payload) => payload?.[0]?.payload?.fullName || ''}
              />
              <Legend verticalAlign="top" />
              <Bar dataKey="Planned"   fill={CHART_COLORS.planned}   radius={[2,2,0,0]} />
              <Bar dataKey="Completed" fill={CHART_COLORS.completed} radius={[2,2,0,0]} />
              <Bar dataKey="Missed"    fill={CHART_COLORS.missed}    radius={[2,2,0,0]} />
              <Bar dataKey="Creep"     fill={CHART_COLORS.creep}     radius={[2,2,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </Paper>
      )}

      {/* Work item breakdown charts */}
      {!loading && breakdownChartData.length > 0 && (
        <>
          {/* Mode selector */}
          <Box display="flex" justifyContent="flex-end" mb={1}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <Select
                value={breakdownMode}
                onChange={e => setBreakdownMode(e.target.value)}
              >
                <MenuItem value="numbers">Numbers</MenuItem>
                <MenuItem value="percentages">Percentages</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Grid container spacing={2} mb={3}>
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  PBI &amp; Bugs by Story Points
                </Typography>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={breakdownChartData} margin={{ top: 4, right: 16, bottom: 40, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" angle={-45} textAnchor="end" interval={0} tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={isPct ? v => `${v}%` : undefined} domain={isPct ? [0, 100] : undefined} />
                    <RechartsTooltip
                      formatter={(val, key) => [isPct ? `${val}%` : `${val} SP`, key.replace(' %', '')]}
                      labelFormatter={(_, payload) => payload?.[0]?.payload?.fullName || ''}
                    />
                    <Legend verticalAlign="top" iconType="circle" formatter={k => k.replace(' %', '')} />
                    <Bar dataKey={isPct ? 'PBI SP %'            : 'PBI SP'}            name={isPct ? 'PBI SP %'            : 'PBI SP'}            stackId="sp" fill="#2e7d32" />
                    <Bar dataKey={isPct ? 'Production Bug SP %' : 'Production Bug SP'} name={isPct ? 'Production Bug SP %' : 'Production Bug SP'} stackId="sp" fill="#d32f2f" />
                    <Bar dataKey={isPct ? 'Internal Bug SP %'   : 'Internal Bug SP'}   name={isPct ? 'Internal Bug SP %'   : 'Internal Bug SP'}   stackId="sp" fill="#1976d2" radius={[2,2,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  PBI &amp; Bugs by Item Count
                </Typography>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={breakdownChartData} margin={{ top: 4, right: 16, bottom: 40, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" angle={-45} textAnchor="end" interval={0} tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} tickFormatter={isPct ? v => `${v}%` : undefined} domain={isPct ? [0, 100] : undefined} />
                    <RechartsTooltip
                      formatter={(val, key) => [isPct ? `${val}%` : `${val} items`, key.replace(' %', '')]}
                      labelFormatter={(_, payload) => payload?.[0]?.payload?.fullName || ''}
                    />
                    <Legend verticalAlign="top" iconType="circle" formatter={k => k.replace(' %', '')} />
                    <Bar dataKey={isPct ? 'PBI Count %'            : 'PBI Count'}            name={isPct ? 'PBI Count %'            : 'PBI Count'}            stackId="cnt" fill="#2e7d32" />
                    <Bar dataKey={isPct ? 'Production Bug Count %' : 'Production Bug Count'} name={isPct ? 'Production Bug Count %' : 'Production Bug Count'} stackId="cnt" fill="#d32f2f" />
                    <Bar dataKey={isPct ? 'Internal Bug Count %'   : 'Internal Bug Count'}   name={isPct ? 'Internal Bug Count %'   : 'Internal Bug Count'}   stackId="cnt" fill="#1976d2" radius={[2,2,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
          </Grid>
        </>
      )}

      {/* Trend charts — Say-Do, Scope Creep, Missed */}
      {!loading && trendData.length > 0 && (
        <Grid container spacing={2} mb={3}>
          {[
            { title: 'Say-Do Ratio Trend', key: 'Say-Do %',       color: '#2e7d32', unit: '%',  refLine: 100, yDomain: [0, 110] },
            { title: 'Scope Creep Trend',  key: 'Scope Creep SP', color: '#f57c00', unit: ' SP', refLine: null, yDomain: undefined },
            { title: 'Missed SP Trend',    key: 'Missed SP',      color: '#d32f2f', unit: ' SP', refLine: null, yDomain: undefined },
          ].map(({ title, key, color, unit, refLine, yDomain }) => (
            <Grid item xs={12} md={4} key={key}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>{title}</Typography>
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={trendData} margin={{ top: 4, right: 12, bottom: 40, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" angle={-45} textAnchor="end" interval={0} tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} domain={yDomain} tickFormatter={unit === '%' ? v => `${v}%` : undefined} />
                    <RechartsTooltip
                      formatter={(val) => [`${val}${unit}`, key]}
                      labelFormatter={(_, payload) => payload?.[0]?.payload?.fullName || ''}
                    />
                    {refLine != null && (
                      <ReferenceLine y={refLine} stroke="#999" strokeDasharray="4 4" label={{ value: '100%', position: 'insideTopRight', fontSize: 10, fill: '#999' }} />
                    )}
                    <Line type="monotone" dataKey={key} stroke={color} strokeWidth={2} dot={{ r: 3, fill: color }} activeDot={{ r: 5 }} />
                  </LineChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {/* PBI & Bugs breakdown table — collapsible */}
      {!loading && breakdown.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Box
            display="flex" alignItems="center" justifyContent="space-between"
            sx={{ cursor: 'pointer', px: 1.5, py: 1, border: '1px solid rgba(0,0,0,0.12)', borderRadius: breakdownTableOpen ? '4px 4px 0 0' : 1, bgcolor: '#fafafa', '&:hover': { bgcolor: '#f0f0f0' } }}
            onClick={() => setBreakdownTableOpen(o => !o)}
          >
            <Typography variant="subtitle2" fontWeight={600}>
              PBI &amp; Bugs Breakdown Table ({breakdown.length} sprints)
            </Typography>
            {breakdownTableOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </Box>
          <Collapse in={breakdownTableOpen}>
            <Paper variant="outlined" sx={{ borderTop: 'none', borderRadius: '0 0 4px 4px' }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ '& th': { fontWeight: 700, whiteSpace: 'nowrap' } }}>
                      <TableCell>Sprint</TableCell>
                      <TableCell align="right" sx={{ color: '#2e7d32' }}>PBI Count</TableCell>
                      <TableCell align="right" sx={{ color: '#2e7d32' }}>PBI SP</TableCell>
                      <TableCell align="right" sx={{ color: '#d32f2f' }}>Prod Bug Count</TableCell>
                      <TableCell align="right" sx={{ color: '#d32f2f' }}>Prod Bug SP</TableCell>
                      <TableCell align="right" sx={{ color: '#1976d2' }}>Internal Bug Count</TableCell>
                      <TableCell align="right" sx={{ color: '#1976d2' }}>Internal Bug SP</TableCell>
                      <TableCell align="right">Total Count</TableCell>
                      <TableCell align="right">Total SP</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[...breakdown].reverse().map(r => {
                      const totalCount = (r.pbiCount || 0) + (r.productionBugCount || 0) + (r.internalBugCount || 0);
                      const totalSP    = (r.pbiSP    || 0) + (r.productionBugSP    || 0) + (r.internalBugSP    || 0);
                      return (
                        <TableRow key={r.sprintName} hover>
                          <TableCell sx={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            <Tooltip title={r.sprintName} placement="right"><span>{r.sprintName}</span></Tooltip>
                          </TableCell>
                          <TableCell align="right">{r.pbiCount || 0}</TableCell>
                          <TableCell align="right">{Math.round(r.pbiSP || 0)}</TableCell>
                          <TableCell align="right">{r.productionBugCount || 0}</TableCell>
                          <TableCell align="right">{Math.round(r.productionBugSP || 0)}</TableCell>
                          <TableCell align="right">{r.internalBugCount || 0}</TableCell>
                          <TableCell align="right">{Math.round(r.internalBugSP || 0)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 500 }}>{totalCount}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 500 }}>{Math.round(totalSP)}</TableCell>
                        </TableRow>
                      );
                    })}
                    {breakdown.length > 0 && (() => {
                      const tot = breakdown.reduce((acc, r) => ({
                        pbiCount:          acc.pbiCount          + (r.pbiCount          || 0),
                        pbiSP:             acc.pbiSP             + (r.pbiSP             || 0),
                        productionBugCount:acc.productionBugCount + (r.productionBugCount || 0),
                        productionBugSP:   acc.productionBugSP   + (r.productionBugSP   || 0),
                        internalBugCount:  acc.internalBugCount  + (r.internalBugCount  || 0),
                        internalBugSP:     acc.internalBugSP     + (r.internalBugSP     || 0),
                      }), { pbiCount:0, pbiSP:0, productionBugCount:0, productionBugSP:0, internalBugCount:0, internalBugSP:0 });
                      const totalCount = tot.pbiCount + tot.productionBugCount + tot.internalBugCount;
                      const totalSP    = tot.pbiSP    + tot.productionBugSP    + tot.internalBugSP;
                      return (
                        <TableRow sx={{ '& td': { fontWeight: 700, borderTop: '2px solid rgba(0,0,0,0.12)' } }}>
                          <TableCell>TOTAL ({breakdown.length} sprints)</TableCell>
                          <TableCell align="right">{tot.pbiCount}</TableCell>
                          <TableCell align="right">{Math.round(tot.pbiSP)}</TableCell>
                          <TableCell align="right">{tot.productionBugCount}</TableCell>
                          <TableCell align="right">{Math.round(tot.productionBugSP)}</TableCell>
                          <TableCell align="right">{tot.internalBugCount}</TableCell>
                          <TableCell align="right">{Math.round(tot.internalBugSP)}</TableCell>
                          <TableCell align="right">{totalCount}</TableCell>
                          <TableCell align="right">{Math.round(totalSP)}</TableCell>
                        </TableRow>
                      );
                    })()}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Collapse>
        </Box>
      )}

      {/* Data table — collapsible */}
      <Box sx={{ mb: 2 }}>
        <Box
          display="flex" alignItems="center" justifyContent="space-between"
          sx={{ cursor: 'pointer', px: 1.5, py: 1, border: '1px solid rgba(0,0,0,0.12)', borderRadius: tableOpen ? '4px 4px 0 0' : 1, bgcolor: '#fafafa', '&:hover': { bgcolor: '#f0f0f0' } }}
          onClick={() => setTableOpen(o => !o)}
        >
          <Typography variant="subtitle2" fontWeight={600}>
            Sprint Data Table ({rows.length} sprints)
          </Typography>
          {tableOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </Box>
        <Collapse in={tableOpen}>
        <Paper variant="outlined" sx={{ borderTop: 'none', borderRadius: '0 0 4px 4px' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, whiteSpace: 'nowrap' } }}>
                <TableCell>Sprint</TableCell>
                <TableCell>Start</TableCell>
                <TableCell>End</TableCell>
                <TableCell align="right">Planned SP</TableCell>
                <TableCell align="right">Completed SP</TableCell>
                <TableCell align="right">Missed / Moved SP</TableCell>
                <TableCell align="right">Scope Creep SP</TableCell>
                <TableCell align="center">Say-Do Ratio</TableCell>
                <TableCell align="center">Planning</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading
                ? Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i}>
                      {Array.from({ length: 9 }).map((__, j) => <TableCell key={j}><Skeleton /></TableCell>)}
                    </TableRow>
                  ))
                : rows.length === 0
                  ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        <Typography variant="body2" color="text.secondary" py={3}>
                          No iteration KPI data found. Click <strong>Refresh from ADO</strong> to fetch metrics.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )
                  : tableRows.map(r => (
                    <TableRow key={r.sprintName} hover>
                      <TableCell sx={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <Tooltip title={r.sprintName} placement="right"><span>{r.sprintName}</span></Tooltip>
                      </TableCell>
                      <TableCell>{r.startDate ? new Date(r.startDate).toLocaleDateString() : '—'}</TableCell>
                      <TableCell>{r.endDate   ? new Date(r.endDate).toLocaleDateString()   : '—'}</TableCell>
                      <TableCell align="right">{fmt(r.plannedPoints)}</TableCell>
                      <TableCell align="right" sx={{ color: CHART_COLORS.completed, fontWeight: 500 }}>
                        {fmt(r.completedPoints)}
                      </TableCell>
                      <TableCell align="right" sx={{ color: r.missedMovedPoints > 0 ? CHART_COLORS.missed : 'inherit' }}>
                        {fmt(r.missedMovedPoints)}
                      </TableCell>
                      <TableCell align="right" sx={{ color: r.scopeCreepPoints > 0 ? CHART_COLORS.creep : 'inherit' }}>
                        {fmt(r.scopeCreepPoints)}
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" sx={{ fontWeight: 600, color: completionTextColor(r.completionRate || 0) }}>
                          {r.completionRate != null ? `${r.completionRate}%` : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <PlanningChip planned={r.plannedPoints} completed={r.completedPoints} />
                      </TableCell>
                    </TableRow>
                  ))
              }

              {!loading && rows.length > 0 && (
                <TableRow sx={{ '& td': { fontWeight: 700, borderTop: '2px solid rgba(0,0,0,0.12)' } }}>
                  <TableCell colSpan={3}>TOTAL ({rows.length} sprints)</TableCell>
                  <TableCell align="right">{fmt(totals.planned)}</TableCell>
                  <TableCell align="right" sx={{ color: CHART_COLORS.completed }}>{fmt(totals.completed)}</TableCell>
                  <TableCell align="right" sx={{ color: CHART_COLORS.missed }}>{fmt(totals.missed)}</TableCell>
                  <TableCell align="right" sx={{ color: CHART_COLORS.creep }}>{fmt(totals.creep)}</TableCell>
                  <TableCell align="center">
                    <Tooltip title={`Average of ${plannedRows.length} sprints with planned work`}>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: completionTextColor(avgSayDo) }}>
                        {avgSayDo}% avg
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <PlanningChip planned={totals.planned} completed={totals.completed} />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        </Paper>
        </Collapse>
      </Box>

      {!loading && rows.length > 0 && (
        <Box display="flex" gap={3} mt={1.5} flexWrap="wrap">
          {Object.entries(PLANNING_CONFIG).map(([, { label, color }]) => (
            <Typography key={label} variant="caption" sx={{ color, fontWeight: 500 }}>● {label}</Typography>
          ))}
          <Typography variant="caption" color="text.disabled">
            Good = Say-Do within ±10% of planned &nbsp;·&nbsp; Say-Do avg excludes sprints with 0 planned SP
            &nbsp;·&nbsp; Source: ADO Analytics (iteration-kpis)
          </Typography>
        </Box>
      )}
    </Box>
  );
};

// ── Top-level view with tabs ──────────────────────────────────────────────────
const SCRUM_TAB_QUERY = 'scrumTab';

// Query keys stay stable across both layouts so existing links keep working.
const TABS_LEGACY = [
  { key: 'iteration', label: 'Iteration Metrics' },
  { key: 'reports', label: 'Sprint Reports' },
  { key: 'review', label: 'Sprint Review' },
  { key: 'governance', label: 'Governance' },
];
// "New Sprint KPI" layout: planning flows left to right. Sprint work items live in Sprint Review
// (team view), so there is no separate Sprint Content tab; old `reports` links open Sprint Review.
const TABS_NEW = [
  { key: 'commitment', label: 'Quarterly Commitment' },
  { key: 'review', label: 'Sprint Review' },
  { key: 'iteration', label: 'Iteration Metrics' },
  { key: 'governance', label: 'Governance' },
];
const TAB_ALIASES = {
  'iteration-metrics': 'iteration', 'sprint-reports': 'reports', content: 'reports', 'sprint-content': 'reports',
  'sprint-review': 'review', 'governance-tracking': 'governance', commitments: 'commitment', 'quarterly-commitment': 'commitment',
};

const tabKeyFromQuery = (raw, tabs) => {
  if (!raw) return tabs[0].key;
  const key = TAB_ALIASES[raw] || raw;
  if (tabs.some((t) => t.key === key)) return key;
  if (key === 'reports' && tabs.some((t) => t.key === 'review')) return 'review';
  const n = parseInt(String(raw), 10);
  return Number.isInteger(n) && tabs[n] ? tabs[n].key : tabs[0].key;
};

const TabLabel = ({ n, label, numbered }) => (
  numbered ? (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
      <Box className="tabn" component="span" sx={{ display: 'inline-grid', placeItems: 'center', width: 18, height: 18, borderRadius: '50%', fontSize: 11, mr: 0.875, bgcolor: 'rgba(26,46,34,0.05)', color: '#5D6762' }}>{n}</Box>
      {label}
    </Box>
  ) : label
);

const SprintMetricsView = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  // Admin setting "New Sprint KPI": null while loading, then true/false.
  const [newSprintKpi, setNewSprintKpi] = useState(null);

  useEffect(() => {
    getSprintHealthConfig()
      .then((res) => setNewSprintKpi(!!res?.data?.enabled))
      .catch(() => setNewSprintKpi(false));
  }, []);

  const tabs = newSprintKpi ? TABS_NEW : TABS_LEGACY;
  const activeKey = tabKeyFromQuery(searchParams.get(SCRUM_TAB_QUERY), tabs);
  const activeIndex = Math.max(0, tabs.findIndex((t) => t.key === activeKey));

  const handleTabChange = (_e, idx) => {
    const key = tabs[idx].key;
    setSearchParams(
      (prev) => {
        const n = new URLSearchParams(prev);
        n.set('section', 'scrum-metrics');
        if (idx === 0) n.delete(SCRUM_TAB_QUERY);
        else n.set(SCRUM_TAB_QUERY, key);
        return n;
      },
      { replace: true },
    );
  };

  return (
    <Box>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3, pt: 3 }}>
        <Typography variant="h4" gutterBottom>Scrum Metrics</Typography>
        {newSprintKpi !== null && (
          <Tabs
            value={activeIndex}
            onChange={handleTabChange}
            textColor="primary"
            // The app runs under `body { zoom }`, which MUI's measured, sliding indicator doesn't
            // account for (it lands short and to the left). Draw the underline on the selected tab
            // itself instead, which is exact at any zoom.
            TabIndicatorProps={{ sx: { display: 'none' } }}
            variant="standard"
            sx={{
              minHeight: 48,
              '& .MuiTab-root': {
                textTransform: 'none',
                // Keep selected/unselected weight identical so the indicator does not drift
                fontWeight: 600,
                fontSize: '0.9375rem',
                minHeight: 48,
                minWidth: 'auto',
                px: 2.5,
                // Avoid transitioning layout props (theme uses transition: all)
                transition: 'color 0.2s ease, background-color 0.2s ease',
                position: 'relative',
              '&.Mui-selected': { fontWeight: 600 },
              '&.Mui-selected::after': {
                content: '""', position: 'absolute', left: 0, right: 0, bottom: 0, height: 3,
                borderRadius: '3px 3px 0 0', bgcolor: newSprintKpi ? 'secondary.main' : 'primary.main',
              },
                '&.Mui-selected .tabn': { bgcolor: '#D74B00', color: '#fff' },
              },
            }}
          >
            {tabs.map((t, i) => <Tab key={t.key} label={<TabLabel n={i + 1} label={t.label} numbered={!!newSprintKpi} />} />)}
          </Tabs>
        )}
      </Box>

      {newSprintKpi !== null && (
        <>
          {activeKey === 'commitment' && <QuarterlyCommitmentTab />}
          {activeKey === 'reports' && <SprintReportDashboard />}
          {activeKey === 'review' && (newSprintKpi ? <SprintHealthTab /> : <SprintReviewTab />)}
          {activeKey === 'iteration' && <IterationMetricsTab />}
          {activeKey === 'governance' && <GovernanceTrackingTab />}
        </>
      )}
    </Box>
  );
};

export default SprintMetricsView;
